import { BONUS_WEEKDAYS } from 'constants/bonus'

/**
 * Determines if a bonus claim should be disabled based on various conditions
 * @param {Object} params - The parameters object
 * @param {Object} params.bonusDetail - Details about the bonus
 * @param {boolean} params.isUserBonus - Whether this is a user bonus
 * @param {Object} params.loyaltyDetails - Details about loyalty program
 * @param {string} params.userBonusId - ID of user bonus
 * @param {Array} params.BONUS_WEEKDAYS - Array of BONUS_WEEKDAYSs
 * @returns {boolean} - Whether bonus claim should be disabled
 */
export const isBonusClaimDisabled = ({
  bonusDetail,
  isUserBonus,
  loyaltyDetails,
  userBonusId
}) => {
  // Check if a bonus is already claimed
  if (bonusDetail?.isClaimed) {
    return true
  }
  // Check if the user doesn't have bonus ID and bonus is not valid for current day of week
  if (!userBonusId && !bonusDetail?.validOnDays?.includes(BONUS_WEEKDAYS[new Date().getDay()])) {
    return true
  }
  // Check if this is a user bonus
  if (isUserBonus) {
    return true
  }
  // Check if the user doesn't have bonus ID and bonus valid date is in future
  if (!userBonusId && new Date(bonusDetail?.validFrom) > new Date()) {
    return true
  }
  // Check if loyalty bonus ID matches the current bonus ID
  if (String(loyaltyDetails?.bonusId) === String(bonusDetail?.bonusId)) {
    return true
  }

  return false
}

/**
 * Formats a bonus into a deposit-compatible bonus object structure
 * @param {Object} bonus - The bonus to format
 * @param {string} bonus.userBonusId - The unique identifier for the user bonus
 * @param {string} bonus.bonusType - The type of bonus
 * @param {string} bonus.status - Current status of the bonus
 * @param {string} bonus.promotionTitle - The title of the promotion
 * @param {number} bonus.depositBonusPercent - The deposit bonus percentage
 * @param {Object} bonus.currency - Currency specific bonus details
 * @param {Object} bonus.other - Additional bonus properties
 * @param {string} languageCode - The language code for localization
 * @returns {Object} Formatted bonus object compatible with a deposit system
 */
export const formatToDepositCompatibleBonusObject = (bonus, languageCode) => {
  return {
    userBonusId: bonus.userBonusId,
    bonusType: bonus.bonusType,
    status: bonus.status,
    bonusId: bonus?.bonusId,
    bonus: {
      // The promotionTitle is structured as an object with language codes as keys.
      // This is required so that the deposit system can display the correct localized title
      // based on the user's selected language. If we only pass a string, multi-language support breaks.
      // This also solves the issue of inconsistent API responses between promotion and deposit flows.
      promotionTitle: {
        [languageCode]: bonus.promotionTitle
      },
      depositBonusPercent: bonus.depositBonusPercent,
      currency: Object.fromEntries(
        Object.entries(bonus.currency || {}).map(([currency, details]) => [
          currency,
          {
            // Ensure numeric values for deposit limits and thresholds.
            // This is required for consistent calculations and validation in the deposit flow.
            minDeposit: details.minDeposit !== undefined ? Number(details.minDeposit) : undefined,
            maxBonusThreshold: details.maxBonusThreshold !== undefined ? Number(details.maxBonusThreshold) : undefined
          }
        ])
      ),
      other: {
        ...bonus.other
        // Spread additional bonus properties for extensibility.
        // This allows the deposit system to handle future bonus features without breaking.
      }
    }
  }
}

export const getFreeSpinsQty = (selectedBonus, currencyCode, identifier) => {
  // aggregatorId: 2 = Hub88, 3 = IGD (iGamingDeck)
  if (selectedBonus?.bonusType === 'freespins' && (selectedBonus?.other?.aggregatorId === '2' || selectedBonus?.other?.aggregatorId === 2)) {
    const selectedBonusDetail = selectedBonus?.other?.hub88PrepaidDetails?.filter(
      (bonusDetail) => bonusDetail.currency === currencyCode && bonusDetail.game_code === identifier
    )
    // Check if selectedBonusDetail exists and has at least one element
    return selectedBonusDetail?.[0]?.bet_count ?? 0
  }
  // IGD aggregator
  if (selectedBonus?.bonusType === 'freespins' && (selectedBonus?.other?.aggregatorId === '3' || selectedBonus?.other?.aggregatorId === 3)) {
    const selectedBonusDetail =
      selectedBonus?.other?.iGamingDeckPrepaidDetails?.filter(
        (bonusDetail) =>
          bonusDetail.currency === currencyCode &&
          bonusDetail.game_code === identifier
      )
    if (selectedBonusDetail?.length > 0) {
      return selectedBonus?.freeSpinsQty ?? 0
    }
  }
  return selectedBonus?.freeSpinsQty ?? 0
}

export const getBonusImageBySequence = (bonusId) => {
  if (typeof bonusId !== 'number' || bonusId < 1) {
    return ''
  }
  const zeroIndexedNumber = bonusId - 1

  const calculatedIndex = zeroIndexedNumber % 2

  return `${process.env.REACT_APP_STATIC_IMAGE}/bonus_static_background_${calculatedIndex + 1}.png`
}
